import { TranscriptTrack } from '@src/editor/models/TranscriptTrack';

import type { VideoTranscriptElement } from '@src/editor/models/elements/VideoTranscriptElement';
import type { Timeline } from '@src/editor/models/Timeline';

/**
 * PLAT-11050 — Sections are derived from the timeline, never stored as a list
 * (DS2). The timeline is the clip (DE3), so a second ordering model would be
 * free to drift from it; this walk is the only way anything reads Sections.
 */
export type ReelSection = {
  /** Provenance id shared by every element in the Section. */
  id: string;
  /** Position in playback order, from 0. */
  index: number;
  startTime: number;
  endTime: number;
  duration: number;
  /** Where this passage starts in the *source* video — what a frame preview needs. */
  mediaStartTime: number;
  /**
   * Every speaker heard in this Section, in order of first appearance. A
   * passage can cross a speaker change, and the row shows all of them as a
   * face pile rather than claiming a single owner.
   */
  speakerLabels: string[];
  /** First few words — the display name fallback when no name is stored (DE8). */
  openingWords: string;
  /** The Section's opening line, shown beneath its name. */
  firstLine: string;
  /**
   * Everything spoken in the Section, lower-cased — what the Reel Editor's
   * search matches on (PLAT-11074 DS4). Built in the pass that already flattens
   * the words, so finding a Section by a phrase inside it costs no extra walk
   * and no second copy of the transcript's word arrays.
   */
  searchText: string;
  elementIds: string[];
};

/** How many words the display fallback shows. */
const OPENING_WORD_COUNT = 6;

/** Longest opening line shown beneath a Section's name before truncating. */
const FIRST_LINE_MAX_CHARS = 100;

/** Media-time gap above which unstamped elements start a new Section (DS3). */
const MEDIA_CONTIGUITY_TOLERANCE = 1e-3;

/**
 * Groups the transcript track's elements, in timeline order, into the reel's
 * Sections.
 *
 * Elements stamped by {@link CropTimelineToReelCommand} group by shared
 * `reelSectionId`. Elements with no stamp — clips saved before PLAT-11050 —
 * fall back to grouping by media-time contiguity (DS3): the naive derivation,
 * correct for anything that has not been word-edited, and it degrades to "more
 * Sections than expected" rather than to an empty panel.
 *
 * O(N) over elements — a reel's transcript runs to thousands of tokens.
 */
export function deriveReelSections(timeline: Timeline): ReelSection[] {
  const track = timeline.tracks.find(
    (t): t is TranscriptTrack => t instanceof TranscriptTrack,
  );
  if (!track) return [];

  const elements = [...track.elements].sort((a, b) => a.time - b.time);

  const sections: ReelSection[] = [];
  let current: ReelSection | null = null;
  let previous: VideoTranscriptElement | null = null;

  for (const element of elements) {
    if (!current || startsNewSection(element, previous)) {
      current = {
        id: sectionIdFor(element),
        index: sections.length,
        startTime: element.time,
        endTime: element.end_time,
        duration: 0,
        mediaStartTime: element.getMediaStartTime(),
        speakerLabels: [],
        openingWords: '',
        firstLine: '',
        searchText: '',
        elementIds: [],
      };
      sections.push(current);
    }

    current.endTime = element.end_time;
    current.elementIds.push(element.id);
    previous = element;
  }

  for (const section of sections) {
    section.duration = section.endTime - section.startTime;
  }

  // Text is read once per Section rather than per element, so a Section made of
  // many elements costs no more than a Section made of one.
  const elementsById = new Map(elements.map(el => [el.id, el]));
  for (const section of sections) {
    const words = section.elementIds.flatMap(id =>
      activeWords(elementsById.get(id)),
    );
    // Distinct, in order of first appearance — a passage can cross a speaker
    // change and the row shows all of them.
    section.speakerLabels = [
      ...new Set(
        words
          .map(word => word.speaker_label)
          .filter((label): label is string => !!label),
      ),
    ];
    section.openingWords = words
      .slice(0, OPENING_WORD_COUNT)
      .map(word => word.value ?? '')
      .join(' ')
      .trim();
    // Empty values dropped rather than joined: a blank token would leave a
    // double space, and a two-word query would then miss a passage that says
    // exactly those two words.
    section.searchText = words
      .map(word => word.value ?? '')
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    // Read from the punctuated token stream, not `words` — sentence detection
    // needs the punctuation, and `getWords()` drops it.
    section.firstLine = firstLineOf(
      section.elementIds.flatMap(id => activeTokens(elementsById.get(id))),
    );
  }

  return sections;
}

/**
 * A stamped element joins the run sharing its stamp; an unstamped one joins
 * only a preceding unstamped element it is media-contiguous with (DS3).
 */
function startsNewSection(
  element: VideoTranscriptElement,
  previous: VideoTranscriptElement | null,
): boolean {
  if (!previous) return true;

  if (element.reelSectionId !== null || previous.reelSectionId !== null) {
    return element.reelSectionId !== previous.reelSectionId;
  }

  return (
    Math.abs(element.getMediaStartTime() - previous.getMediaEndTime()) >
    MEDIA_CONTIGUITY_TOLERANCE
  );
}

/**
 * Unstamped Sections need an id that survives a re-derivation, because both the
 * stored names (`clipJson.reel.sections`) and the panel's React keys are keyed
 * by it. Media start is the stable choice — element ids are re-minted by every
 * reshaping path, so keying off one would mint a new Section on every edit.
 */
function sectionIdFor(element: VideoTranscriptElement): string {
  return (
    element.reelSectionId ?? `media:${element.getMediaStartTime().toFixed(3)}`
  );
}

/**
 * The Section's opening line: up to the first sentence break, or a clean word
 * boundary within {@link FIRST_LINE_MAX_CHARS} if the sentence runs long.
 *
 * Takes the *punctuated* token stream, because sentence detection needs the
 * punctuation that `getWords()` drops. Words are space-separated and
 * punctuation is butted against the preceding word: whitespace tokens do not
 * survive re-projection (`trimTextAndFilterOutWhitespaces` strips them), so
 * concatenating raw values would run every word together.
 */
function firstLineOf(
  tokens: { type?: string; value: string | null }[],
): string {
  let text = '';
  for (const token of tokens) {
    const value = token.value ?? '';
    if (!value) continue;
    if (token.type === 'text' && text) text += ' ';
    text += value;
  }
  text = text.replace(/\s+/g, ' ').trim();
  if (!text) return '';

  const sentenceEnd = text.search(/[.!?](\s|$)/);
  if (sentenceEnd !== -1 && sentenceEnd + 1 <= FIRST_LINE_MAX_CHARS) {
    return text.slice(0, sentenceEnd + 1);
  }

  if (text.length <= FIRST_LINE_MAX_CHARS) return text;

  const clipped = text.slice(0, FIRST_LINE_MAX_CHARS);
  const lastSpace = clipped.lastIndexOf(' ');
  return `${(lastSpace > 0 ? clipped.slice(0, lastSpace) : clipped).trim()}…`;
}

const isActive = (token: { state?: string }) =>
  token.state !== 'removed' && token.state !== 'cut';

/** Text tokens only — words, no punctuation. */
function activeWords(element: VideoTranscriptElement | undefined) {
  if (!element) return [];
  return element.getClipTranscriptSegment().getWords().filter(isActive);
}

/** Words, punctuation and whitespace, in reading order. */
function activeTokens(element: VideoTranscriptElement | undefined) {
  if (!element) return [];
  return element
    .getClipTranscriptSegment()
    .getWordsWithPunctuation()
    .filter(isActive);
}
